import { useEffect, useState } from 'react';
import { cn } from '../../lib/utils';
import { CHAPTER_BAR, HEADER } from './motion';
import { PLANS, planByKey } from './plans';

// The homepage is told in three chapters, one per plan. Each scrolls through
// as normal, under a dark title bar. Once a chapter's bar reaches its place under the header (below
// the bars of the chapters before it) it stays there for the rest of the page,
// so by the end Solo, Filmmaker and Teams sit stacked under the header. Click
// one to go back to the start of that chapter.

const slotOf = (key) => PLANS.findIndex((p) => p.key === key);

// Where a chapter's bar pins: under the header and the bars above it
export const barTop = (key) => HEADER + slotOf(key) * CHAPTER_BAR;

// How much of the window a chapter's content loses to the header and the
// pinned bars, its own included
export const coveredIn = (key) => barTop(key) + CHAPTER_BAR;

// Scroll so a chapter starts again with its bar in its place
export function goToChapter(key) {
  const el = document.getElementById(key);
  if (!el) return;
  const start = el.getBoundingClientRect().top + window.scrollY;
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({ top: start - barTop(key), behavior: reduce ? 'auto' : 'smooth' });
}

function ChapterBar({ plan, className }) {
  return (
    <button
      type="button"
      onClick={() => goToChapter(plan.key)}
      className={cn('group w-full h-full bg-inverse text-inverse-foreground text-left', className)}
      aria-label={`Go to ${plan.name}`}
    >
      <div className="h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-3 sm:gap-4 min-w-0">
        {/* The name and its line share a baseline */}
        <div className="flex items-baseline gap-3 sm:gap-4 min-w-0 flex-1">
          <h2 className="text-xl sm:text-2xl font-normal shrink-0">{plan.name}</h2>
          <p className="text-inverse-muted truncate min-w-0 group-hover:text-inverse-foreground transition-colors">{plan.title}</p>
        </div>
        <span
          className={cn(
            'shrink-0 rounded-pill px-2.5 py-0.5 text-xs text-foreground',
            plan.onSale ? 'bg-moss-soft' : 'bg-ochre-soft',
          )}
        >
          {plan.onSale ? 'Available now' : 'Coming soon'}
        </span>
      </div>
    </button>
  );
}

// A chapter scrolls through like an ordinary page: only its bar stays. It sits
// on the colour of the section before it (`under`), for its rounded top.
export function Chapter({ plan: key, under, children }) {
  const plan = planByKey(key);
  return (
    <section id={plan.key} data-chapter={plan.key} className={cn('relative', under)}>
      <div className="rounded-t-[32px] lg:rounded-t-[48px] [overflow:clip]">
        {/* Its bar in the flow, until it reaches its place; ChapterBars takes over there */}
        <div style={{ height: CHAPTER_BAR }}>
          <ChapterBar plan={plan} />
        </div>
        {children}
      </div>
    </section>
  );
}

// The pinned bars, over everything but the header. A bar shows once its
// chapter's top has reached the bar's place, exactly where the chapter's own
// bar is at that moment, so the hand-over can't be seen. Its rounded corners
// are filled, so nothing scrolls by behind them: the first on the header's
// colour, each after it on the bar above's, its rounded top a thin line between.
export function ChapterBars() {
  const [reached, setReached] = useState([]);
  useEffect(() => {
    let frame = 0;
    const check = () => {
      frame = 0;
      const now = PLANS.filter((p) => {
        const el = document.getElementById(p.key);
        return el && el.getBoundingClientRect().top <= barTop(p.key) + 0.5;
      }).map((p) => p.key);
      setReached((was) => (was.join() === now.join() ? was : now));
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(check);
    };
    check();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <nav aria-label="Plans" className="contents">
      {PLANS.filter((p) => reached.includes(p.key)).map((plan, i) => (
        <div
          key={plan.key}
          data-pinned-bar
          className={cn('fixed inset-x-0 z-40', i > 0 ? 'bg-inverse' : 'bg-background')}
          style={{ top: barTop(plan.key), height: CHAPTER_BAR }}
        >
          <ChapterBar
            plan={plan}
            className={cn('rounded-t-[32px] lg:rounded-t-[48px]', i > 0 && 'border-t border-inverse-foreground/20')}
          />
        </div>
      ))}
    </nav>
  );
}
